import type {
  CheckConstraintModel,
  ColumnModel,
  ForeignKeyModel,
  IndexModel,
  PrimaryKeyModel,
  SchemaModel,
  TableModel,
  UniqueConstraintModel,
} from '@bits2schema/shared';
import type { Command as GenericCommand } from '../command/types';
import { moveColumn, removeColumnFromConstraints, renameColumnCascade, renameTableCascade, updateTable } from './mutations';

/**
 * Command vrstva pro strukturální editace (Epic 4), stejný tvar a stejná
 * pravidla jako `canvas/commands.ts` (Epic 6) — viz `command/types.ts`
 * hlavička. Validace (unikátní název, kompatibilita typů u FK, ...) proběhne
 * v UI handleru PŘED vytvořením commandu; `apply`/`revert` jsou tu vždy
 * totální a nikdy nevyhazují.
 */
export type SchemaCommandType =
  | 'addTable'
  | 'renameTable'
  | 'deleteTable'
  | 'updateTableComment'
  | 'addColumn'
  | 'renameColumn'
  | 'updateColumn'
  | 'deleteColumn'
  | 'moveColumn'
  | 'setPrimaryKey'
  | 'addUniqueConstraint'
  | 'updateUniqueConstraint'
  | 'deleteUniqueConstraint'
  | 'addCheckConstraint'
  | 'updateCheckConstraint'
  | 'deleteCheckConstraint'
  | 'addForeignKey'
  | 'updateForeignKey'
  | 'deleteForeignKey'
  | 'addIndex'
  | 'updateIndex'
  | 'deleteIndex';

/** Konkrétní instance generického `Command<TState>` (`../command/types`) nad `SchemaModel`. */
export type SchemaCommand = GenericCommand<SchemaModel, SchemaCommandType>;

export interface ColumnEditableFields {
  rawType: string;
  canonicalType: string | null;
  isTypeRecognized: boolean;
  nullable: boolean;
  defaultValue: string | null;
  comment: string | null;
}

// --- Tabulka ----------------------------------------------------------------

export function createAddTableCommand(table: TableModel): SchemaCommand {
  return {
    type: 'addTable',
    descriptionKey: 'schema.command.addTable',
    descriptionParams: { name: table.name },
    apply: (schema) => ({ ...schema, tables: [...schema.tables, table] }),
    revert: (schema) => ({ ...schema, tables: schema.tables.filter((candidate) => candidate.id !== table.id) }),
  };
}

/** Mazání se validuje (blokace na závislé FK) v UI PŘED voláním — viz `schema/mutations.ts#findTableDependents`. */
export function createDeleteTableCommand(table: TableModel, originalIndex: number): SchemaCommand {
  return {
    type: 'deleteTable',
    descriptionKey: 'schema.command.deleteTable',
    descriptionParams: { name: table.name },
    apply: (schema) => ({ ...schema, tables: schema.tables.filter((candidate) => candidate.id !== table.id) }),
    revert: (schema) => {
      const tables = [...schema.tables];
      tables.splice(originalIndex, 0, table);
      return { ...schema, tables };
    },
  };
}

export function createRenameTableCommand(
  tableId: string,
  from: string,
  to: string,
  previousRenamedFrom: string | undefined,
): SchemaCommand {
  return {
    type: 'renameTable',
    descriptionKey: 'schema.command.renameTable',
    descriptionParams: { from, to },
    apply: (schema) => renameTableCascade(schema, tableId, to, previousRenamedFrom ?? from),
    revert: (schema) => renameTableCascade(schema, tableId, from, previousRenamedFrom),
  };
}

export function createUpdateTableCommentCommand(
  tableId: string,
  from: string | null,
  to: string | null,
): SchemaCommand {
  return {
    type: 'updateTableComment',
    descriptionKey: 'schema.command.updateTableComment',
    apply: (schema) => updateTable(schema, tableId, (table) => ({ ...table, comment: to })),
    revert: (schema) => updateTable(schema, tableId, (table) => ({ ...table, comment: from })),
  };
}

// --- Sloupec ------------------------------------------------------------------

export function createAddColumnCommand(tableId: string, column: ColumnModel): SchemaCommand {
  return {
    type: 'addColumn',
    descriptionKey: 'schema.command.addColumn',
    descriptionParams: { name: column.name },
    apply: (schema) => updateTable(schema, tableId, (table) => ({ ...table, columns: [...table.columns, column] })),
    revert: (schema) =>
      updateTable(schema, tableId, (table) => ({
        ...table,
        columns: table.columns.filter((candidate) => candidate.id !== column.id),
      })),
  };
}

/** FK-blokace (E4-US6) i strip z PK/unique/index proběhne v `apply()` přes `removeColumnFromConstraints`. */
export function createDeleteColumnCommand(
  tableId: string,
  column: ColumnModel,
  originalIndex: number,
  previousPrimaryKey: PrimaryKeyModel | null,
  previousUniqueConstraints: UniqueConstraintModel[],
  previousIndexes: IndexModel[],
): SchemaCommand {
  return {
    type: 'deleteColumn',
    descriptionKey: 'schema.command.deleteColumn',
    descriptionParams: { name: column.name },
    apply: (schema) =>
      updateTable(schema, tableId, (table) => {
        const stripped = removeColumnFromConstraints(table, column.name);
        return { ...stripped, columns: table.columns.filter((candidate) => candidate.id !== column.id) };
      }),
    revert: (schema) =>
      updateTable(schema, tableId, (table) => {
        const columns = [...table.columns];
        columns.splice(originalIndex, 0, column);
        return {
          ...table,
          columns,
          primaryKey: previousPrimaryKey,
          uniqueConstraints: previousUniqueConstraints,
          indexes: previousIndexes,
        };
      }),
  };
}

export function createRenameColumnCommand(
  tableId: string,
  columnId: string,
  from: string,
  to: string,
  previousRenamedFrom: string | undefined,
): SchemaCommand {
  return {
    type: 'renameColumn',
    descriptionKey: 'schema.command.renameColumn',
    descriptionParams: { from, to },
    apply: (schema) => renameColumnCascade(schema, tableId, columnId, to, previousRenamedFrom ?? from),
    revert: (schema) => renameColumnCascade(schema, tableId, columnId, from, previousRenamedFrom),
  };
}

export function createUpdateColumnCommand(
  tableId: string,
  columnId: string,
  from: ColumnEditableFields,
  to: ColumnEditableFields,
): SchemaCommand {
  const withFields = (columns: ColumnModel[], fields: ColumnEditableFields) =>
    columns.map((column) => (column.id === columnId ? { ...column, ...fields } : column));
  return {
    type: 'updateColumn',
    descriptionKey: 'schema.command.updateColumn',
    apply: (schema) => updateTable(schema, tableId, (table) => ({ ...table, columns: withFields(table.columns, to) })),
    revert: (schema) =>
      updateTable(schema, tableId, (table) => ({ ...table, columns: withFields(table.columns, from) })),
  };
}

/**
 * Epic 15, E15-US5 — prohození dvou sousedních sloupců je vlastní inverzí:
 * `revert()` je stejná operace s opačným směrem, žádné samostatné uložení
 * „předchozího pořadí" celé tabulky netřeba (na rozdíl od
 * `createDeleteColumnCommand`, který si musí pamatovat `originalIndex` kvůli
 * mazání, ne přeuspořádání).
 */
export function createMoveColumnCommand(
  tableId: string,
  columnId: string,
  columnName: string,
  direction: 'up' | 'down',
): SchemaCommand {
  const opposite = direction === 'up' ? 'down' : 'up';
  return {
    type: 'moveColumn',
    descriptionKey: 'schema.command.moveColumn',
    descriptionParams: { name: columnName },
    apply: (schema) => updateTable(schema, tableId, (table) => moveColumn(table, columnId, direction)),
    revert: (schema) => updateTable(schema, tableId, (table) => moveColumn(table, columnId, opposite)),
  };
}

export function createSetPrimaryKeyCommand(
  tableId: string,
  from: PrimaryKeyModel | null,
  to: PrimaryKeyModel | null,
): SchemaCommand {
  return {
    type: 'setPrimaryKey',
    descriptionKey: 'schema.command.setPrimaryKey',
    apply: (schema) => updateTable(schema, tableId, (table) => ({ ...table, primaryKey: to })),
    revert: (schema) => updateTable(schema, tableId, (table) => ({ ...table, primaryKey: from })),
  };
}

// --- Unique constraint --------------------------------------------------------

export function createAddUniqueConstraintCommand(tableId: string, constraint: UniqueConstraintModel): SchemaCommand {
  return {
    type: 'addUniqueConstraint',
    descriptionKey: 'schema.command.addUniqueConstraint',
    descriptionParams: { name: constraint.name },
    apply: (schema) =>
      updateTable(schema, tableId, (table) => ({
        ...table,
        uniqueConstraints: [...table.uniqueConstraints, constraint],
      })),
    revert: (schema) =>
      updateTable(schema, tableId, (table) => ({
        ...table,
        uniqueConstraints: table.uniqueConstraints.filter((candidate) => candidate.id !== constraint.id),
      })),
  };
}

export function createUpdateUniqueConstraintCommand(
  tableId: string,
  constraintId: string,
  from: UniqueConstraintModel,
  to: UniqueConstraintModel,
): SchemaCommand {
  const withValue = (list: UniqueConstraintModel[], value: UniqueConstraintModel) =>
    list.map((candidate) => (candidate.id === constraintId ? value : candidate));
  return {
    type: 'updateUniqueConstraint',
    descriptionKey: 'schema.command.updateUniqueConstraint',
    descriptionParams: { name: to.name },
    apply: (schema) =>
      updateTable(schema, tableId, (table) => ({ ...table, uniqueConstraints: withValue(table.uniqueConstraints, to) })),
    revert: (schema) =>
      updateTable(schema, tableId, (table) => ({ ...table, uniqueConstraints: withValue(table.uniqueConstraints, from) })),
  };
}

export function createDeleteUniqueConstraintCommand(
  tableId: string,
  constraint: UniqueConstraintModel,
  originalIndex: number,
): SchemaCommand {
  return {
    type: 'deleteUniqueConstraint',
    descriptionKey: 'schema.command.deleteUniqueConstraint',
    descriptionParams: { name: constraint.name },
    apply: (schema) =>
      updateTable(schema, tableId, (table) => ({
        ...table,
        uniqueConstraints: table.uniqueConstraints.filter((candidate) => candidate.id !== constraint.id),
      })),
    revert: (schema) =>
      updateTable(schema, tableId, (table) => {
        const uniqueConstraints = [...table.uniqueConstraints];
        uniqueConstraints.splice(originalIndex, 0, constraint);
        return { ...table, uniqueConstraints };
      }),
  };
}

// --- Check constraint ----------------------------------------------------------

export function createAddCheckConstraintCommand(tableId: string, constraint: CheckConstraintModel): SchemaCommand {
  return {
    type: 'addCheckConstraint',
    descriptionKey: 'schema.command.addCheckConstraint',
    descriptionParams: { name: constraint.name },
    apply: (schema) =>
      updateTable(schema, tableId, (table) => ({ ...table, checkConstraints: [...table.checkConstraints, constraint] })),
    revert: (schema) =>
      updateTable(schema, tableId, (table) => ({
        ...table,
        checkConstraints: table.checkConstraints.filter((candidate) => candidate.id !== constraint.id),
      })),
  };
}

export function createUpdateCheckConstraintCommand(
  tableId: string,
  constraintId: string,
  from: CheckConstraintModel,
  to: CheckConstraintModel,
): SchemaCommand {
  const withValue = (list: CheckConstraintModel[], value: CheckConstraintModel) =>
    list.map((candidate) => (candidate.id === constraintId ? value : candidate));
  return {
    type: 'updateCheckConstraint',
    descriptionKey: 'schema.command.updateCheckConstraint',
    descriptionParams: { name: to.name },
    apply: (schema) =>
      updateTable(schema, tableId, (table) => ({ ...table, checkConstraints: withValue(table.checkConstraints, to) })),
    revert: (schema) =>
      updateTable(schema, tableId, (table) => ({ ...table, checkConstraints: withValue(table.checkConstraints, from) })),
  };
}

export function createDeleteCheckConstraintCommand(
  tableId: string,
  constraint: CheckConstraintModel,
  originalIndex: number,
): SchemaCommand {
  return {
    type: 'deleteCheckConstraint',
    descriptionKey: 'schema.command.deleteCheckConstraint',
    descriptionParams: { name: constraint.name },
    apply: (schema) =>
      updateTable(schema, tableId, (table) => ({
        ...table,
        checkConstraints: table.checkConstraints.filter((candidate) => candidate.id !== constraint.id),
      })),
    revert: (schema) =>
      updateTable(schema, tableId, (table) => {
        const checkConstraints = [...table.checkConstraints];
        checkConstraints.splice(originalIndex, 0, constraint);
        return { ...table, checkConstraints };
      }),
  };
}

// --- Foreign key -----------------------------------------------------------------

export function createAddForeignKeyCommand(tableId: string, foreignKey: ForeignKeyModel): SchemaCommand {
  return {
    type: 'addForeignKey',
    descriptionKey: 'schema.command.addForeignKey',
    descriptionParams: { name: foreignKey.name },
    apply: (schema) =>
      updateTable(schema, tableId, (table) => ({ ...table, foreignKeys: [...table.foreignKeys, foreignKey] })),
    revert: (schema) =>
      updateTable(schema, tableId, (table) => ({
        ...table,
        foreignKeys: table.foreignKeys.filter((candidate) => candidate.id !== foreignKey.id),
      })),
  };
}

export function createUpdateForeignKeyCommand(
  tableId: string,
  foreignKeyId: string,
  from: ForeignKeyModel,
  to: ForeignKeyModel,
): SchemaCommand {
  const withValue = (list: ForeignKeyModel[], value: ForeignKeyModel) =>
    list.map((candidate) => (candidate.id === foreignKeyId ? value : candidate));
  return {
    type: 'updateForeignKey',
    descriptionKey: 'schema.command.updateForeignKey',
    descriptionParams: { name: to.name },
    apply: (schema) => updateTable(schema, tableId, (table) => ({ ...table, foreignKeys: withValue(table.foreignKeys, to) })),
    revert: (schema) =>
      updateTable(schema, tableId, (table) => ({ ...table, foreignKeys: withValue(table.foreignKeys, from) })),
  };
}

export function createDeleteForeignKeyCommand(
  tableId: string,
  foreignKey: ForeignKeyModel,
  originalIndex: number,
): SchemaCommand {
  return {
    type: 'deleteForeignKey',
    descriptionKey: 'schema.command.deleteForeignKey',
    descriptionParams: { name: foreignKey.name },
    apply: (schema) =>
      updateTable(schema, tableId, (table) => ({
        ...table,
        foreignKeys: table.foreignKeys.filter((candidate) => candidate.id !== foreignKey.id),
      })),
    revert: (schema) =>
      updateTable(schema, tableId, (table) => {
        const foreignKeys = [...table.foreignKeys];
        foreignKeys.splice(originalIndex, 0, foreignKey);
        return { ...table, foreignKeys };
      }),
  };
}

// --- Index -------------------------------------------------------------------------

export function createAddIndexCommand(tableId: string, index: IndexModel): SchemaCommand {
  return {
    type: 'addIndex',
    descriptionKey: 'schema.command.addIndex',
    descriptionParams: { name: index.name },
    apply: (schema) => updateTable(schema, tableId, (table) => ({ ...table, indexes: [...table.indexes, index] })),
    revert: (schema) =>
      updateTable(schema, tableId, (table) => ({
        ...table,
        indexes: table.indexes.filter((candidate) => candidate.id !== index.id),
      })),
  };
}

export function createUpdateIndexCommand(
  tableId: string,
  indexId: string,
  from: IndexModel,
  to: IndexModel,
): SchemaCommand {
  const withValue = (list: IndexModel[], value: IndexModel) =>
    list.map((candidate) => (candidate.id === indexId ? value : candidate));
  return {
    type: 'updateIndex',
    descriptionKey: 'schema.command.updateIndex',
    descriptionParams: { name: to.name },
    apply: (schema) => updateTable(schema, tableId, (table) => ({ ...table, indexes: withValue(table.indexes, to) })),
    revert: (schema) => updateTable(schema, tableId, (table) => ({ ...table, indexes: withValue(table.indexes, from) })),
  };
}

export function createDeleteIndexCommand(tableId: string, index: IndexModel, originalIndex: number): SchemaCommand {
  return {
    type: 'deleteIndex',
    descriptionKey: 'schema.command.deleteIndex',
    descriptionParams: { name: index.name },
    apply: (schema) =>
      updateTable(schema, tableId, (table) => ({
        ...table,
        indexes: table.indexes.filter((candidate) => candidate.id !== index.id),
      })),
    revert: (schema) =>
      updateTable(schema, tableId, (table) => {
        const indexes = [...table.indexes];
        indexes.splice(originalIndex, 0, index);
        return { ...table, indexes };
      }),
  };
}

